import { initHeroVisuals } from '../common/heroVisuals';
import { cultureHeaders, newsletterConfirmPath } from '../common/localizedRoutes';

function initMenuIndicator(): void {
    const menu = document.querySelector<HTMLElement>('.menu');
    if (!menu) {
        return;
    }

    const items = Array.from(menu.querySelectorAll<HTMLElement>('a[data-route], .menu-trigger[data-route]'));
    if (!items.length) {
        return;
    }

    const activeRoute = document.body.dataset.page ?? '';
    let active = items.find(item => (item.dataset.route ?? '') === activeRoute) ?? items[0];
    active.classList.add('is-active');
    if (active instanceof HTMLAnchorElement) {
        active.setAttribute('aria-current', 'page');
    }

    menu.addEventListener('click', event => {
        const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('a[data-route], .menu-trigger[data-route]');
        if (!target) {
            return;
        }

        items.forEach(item => {
            item.classList.remove('is-active');
            item.removeAttribute('aria-current');
        });
        target.classList.add('is-active');
        if (target instanceof HTMLAnchorElement) {
            target.setAttribute('aria-current', 'page');
        }
        active = target;
    });
}

function initMegaMenu(): void {
    const menuItems = Array.from(document.querySelectorAll<HTMLElement>('.has-mega'));
    if (!menuItems.length) {
        return;
    }

    const closeTimers = new Map<HTMLElement, number>();

    const clearCloseTimer = (item: HTMLElement): void => {
        const timer = closeTimers.get(item);
        if (timer !== undefined) {
            window.clearTimeout(timer);
            closeTimers.delete(item);
        }
    };

    const closeItem = (item: HTMLElement): void => {
        clearCloseTimer(item);
        item.classList.remove('is-open');
        item.querySelector<HTMLButtonElement>('.menu-trigger')?.setAttribute('aria-expanded', 'false');
    };

    const closeAll = (except?: HTMLElement): void => {
        menuItems.forEach(item => {
            if (item === except) {
                return;
            }
            closeItem(item);
        });
    };

    const openItem = (item: HTMLElement, trigger: HTMLButtonElement): void => {
        clearCloseTimer(item);
        closeAll(item);
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
    };

    const scheduleClose = (item: HTMLElement): void => {
        clearCloseTimer(item);
        closeTimers.set(item, window.setTimeout(() => closeItem(item), 140));
    };

    menuItems.forEach(item => {
        const trigger = item.querySelector<HTMLButtonElement>('.menu-trigger');
        if (!trigger) {
            return;
        }

        item.addEventListener('mouseenter', () => {
            openItem(item, trigger);
        });

        item.addEventListener('mouseleave', () => {
            scheduleClose(item);
        });

        item.addEventListener('focusin', () => {
            openItem(item, trigger);
        });

        item.addEventListener('focusout', event => {
            const nextTarget = event.relatedTarget as Node | null;
            if (nextTarget && item.contains(nextTarget)) {
                return;
            }

            closeItem(item);
        });

        trigger.addEventListener('click', event => {
            event.stopPropagation();
            const nextOpen = !item.classList.contains('is-open');
            if (nextOpen) {
                openItem(item, trigger);
                return;
            }
            closeItem(item);
        });
    });

    document.addEventListener('click', event => {
        const target = event.target as Node | null;
        if (!target || menuItems.some(item => item.contains(target))) {
            return;
        }
        closeAll();
    });

    document.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
            closeAll();
        }
    });
}

function initFooterYear(): void {
    const footerYear = document.getElementById('foot-year');
    if (footerYear) {
        footerYear.textContent = new Date().getFullYear().toString();
    }
}

type ConsentValue = 'granted' | 'denied';

interface ConsentRecord {
    version: 1;
    analytics: boolean;
    decidedAt: string;
}

const consentStorageKey = 'rinkachi-consent-v1';
const legacyConsentStorageKey = 'cookie-consent';
const analyticsCookieNames = ['_ga', '_gid', '_gat', '_gcl_au'];
const analyticsCookiePrefixes = ['_ga_', '_gac_'];
let analyticsConsentGranted = false;
let consentDefaultsInitialized = false;
let analyticsConfigured = false;

const ensureGtag = (): ((...args: unknown[]) => void) => {
    const host = window as any;
    host.dataLayer = host.dataLayer || [];

    if (typeof host.gtag !== 'function') {
        host.gtag = (...args: unknown[]): void => {
            host.dataLayer.push(args);
        };
    }

    return host.gtag;
};

const consentState = (analyticsStorage: ConsentValue): Record<string, ConsentValue> => ({
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: analyticsStorage
});

function initializeConsentDefaults(): void {
    if (consentDefaultsInitialized) {
        return;
    }

    ensureGtag()('consent', 'default', consentState('denied'));
    consentDefaultsInitialized = true;
}

function updateAnalyticsConsent(value: ConsentValue): void {
    initializeConsentDefaults();
    ensureGtag()('consent', 'update', consentState(value));
}

function readStoredConsent(): ConsentRecord | null {
    try {
        const raw = window.localStorage.getItem(consentStorageKey);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
        if (parsed.version !== 1 || typeof parsed.analytics !== 'boolean' || typeof parsed.decidedAt !== 'string') {
            return null;
        }

        return parsed as ConsentRecord;
    } catch {
        return null;
    }
}

function writeStoredConsent(analytics: boolean): void {
    const record: ConsentRecord = {
        version: 1,
        analytics,
        decidedAt: new Date().toISOString()
    };

    try {
        window.localStorage.setItem(consentStorageKey, JSON.stringify(record));
        window.localStorage.removeItem(legacyConsentStorageKey);
    } catch {
        // If storage is unavailable, apply the in-memory decision for the current page only.
    }
}

function setAnalyticsDisabled(measurementId: string, disabled: boolean): void {
    if (!measurementId) {
        return;
    }

    (window as any)[`ga-disable-${measurementId}`] = disabled;
}

function cookieDomainCandidates(): Array<string | null> {
    const host = window.location.hostname;
    const domains: Array<string | null> = [null];

    if (!host || host === 'localhost' || /^[\d.]+$/.test(host)) {
        return domains;
    }

    domains.push(host, `.${host}`);
    const parts = host.split('.');
    if (parts.length > 2) {
        domains.push(`.${parts.slice(-2).join('.')}`);
    }

    return Array.from(new Set(domains));
}

function removeGoogleAnalyticsCookies(): void {
    const names = document.cookie
        .split(';')
        .map(cookie => cookie.split('=')[0]?.trim())
        .filter((name): name is string => Boolean(name))
        .filter(name => analyticsCookieNames.includes(name) || analyticsCookiePrefixes.some(prefix => name.startsWith(prefix)));

    if (!names.length) {
        return;
    }

    const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    const maxAge = 'Max-Age=0';
    const path = 'path=/';
    const sameSite = 'SameSite=Lax';

    names.forEach(name => {
        cookieDomainCandidates().forEach(domain => {
            const domainPart = domain ? `; domain=${domain}` : '';
            document.cookie = `${name}=; ${expires}; ${maxAge}; ${path}; ${sameSite}${domainPart}`;
        });
    });
}

function disableAnalytics(measurementId: string): void {
    analyticsConsentGranted = false;
    setAnalyticsDisabled(measurementId, true);
    updateAnalyticsConsent('denied');
    removeGoogleAnalyticsCookies();
}

function loadAnalytics(measurementId: string): void {
    if (!measurementId) {
        return;
    }

    analyticsConsentGranted = true;
    setAnalyticsDisabled(measurementId, false);
    updateAnalyticsConsent('granted');

    const existing = document.querySelector<HTMLScriptElement>(`script[data-gtag="${measurementId}"]`);
    if (!existing) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
        script.dataset.gtag = measurementId;
        document.head.appendChild(script);
    }

    if (analyticsConfigured) {
        return;
    }

    const gtag = ensureGtag();
    gtag('js', new Date());
    gtag('config', measurementId, {
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false
    });
    analyticsConfigured = true;
}

const trackEvent = (name: string, params: Record<string, unknown> = {}): void => {
    if (!analyticsConsentGranted) {
        return;
    }

    const gtag = (window as any).gtag as ((...args: unknown[]) => void) | undefined;
    if (typeof gtag !== 'function') {
        return;
    }

    gtag('event', name, params);
};

function initAnalyticsEvents(): void {
    document.addEventListener('click', event => {
        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const cta = target.closest<HTMLButtonElement | HTMLAnchorElement>('.btn');
        if (cta && !cta.hasAttribute('data-cookie-accept') && !cta.hasAttribute('data-cookie-decline')) {
            const label = (cta.textContent || '').trim();
            const href = cta instanceof HTMLAnchorElement ? cta.href : null;
            const ctaKey = cta.getAttribute('data-cta') || cta.id || null;
            trackEvent('cta_click', { label, href, cta: ctaKey });
        }

        const newsletterOpen = target.closest<HTMLElement>('[data-newsletter-open]');
        if (newsletterOpen) {
            trackEvent('newsletter_open', { source: newsletterOpen.getAttribute('data-cta') ?? 'button' });
        }

        const projectLink = target.closest<HTMLAnchorElement>('a[href^="/craft/projects/"], a[href^="/rzemioslo/projekty/"]');
        if (projectLink) {
            const href = projectLink.getAttribute('href') ?? '';
            const slug = href
                .replace(/^\/craft\/projects\//, '')
                .replace(/^\/rzemioslo\/projekty\//, '')
                .split(/[?#]/)[0];
            if (slug) {
                const itemId = decodeURIComponent(slug);
                trackEvent('project_click', { slug: itemId });
                trackEvent('select_content', { content_type: 'project', item_id: itemId });
            }
        }
    });
}

function initFaqSchema(): void {
    const faqItems = Array.from(document.querySelectorAll<HTMLDetailsElement>('.faq-list .faq-item'));
    if (!faqItems.length) {
        return;
    }

    const mainEntity = faqItems.map(item => {
        const question = item.querySelector<HTMLElement>('summary')?.textContent?.trim() ?? '';
        const answer = item.querySelector<HTMLElement>('.faq-panel')?.textContent?.trim() ?? '';
        if (!question || !answer) {
            return null;
        }
        return {
            "@type": "Question",
            "name": question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": answer
            }
        };
    }).filter(Boolean);

    if (!mainEntity.length) {
        return;
    }

    const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": mainEntity
    };

    let script = document.getElementById('faq-schema') as HTMLScriptElement | null;
    if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'faq-schema';
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
}

function initParallaxSections(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const selector = [
        '.hero-landing',
        '.page-container > .hero-section',
        '.page-container > section[class*="-hero"]',
        '.page-container > header[class*="-hero"]'
    ].join(', ');

    const sections = Array.from(document.querySelectorAll<HTMLElement>(selector));
    if (!sections.length) {
        return;
    }

    let ticking = false;

    const update = (): void => {
        const viewportHeight = window.innerHeight || 1;

        sections.forEach(section => {
            const rect = section.getBoundingClientRect();
            if (rect.bottom < -viewportHeight * 0.25 || rect.top > viewportHeight * 1.25) {
                return;
            }

            const sectionCenter = rect.top + rect.height / 2;
            const viewportCenter = viewportHeight / 2;
            const raw = (viewportCenter - sectionCenter) / (viewportHeight + rect.height);
            const clamped = Math.max(-1, Math.min(1, raw));
            const offset = clamped * 86;

            section.style.setProperty('--parallax-y', `${offset.toFixed(2)}px`);
            section.style.setProperty('--parallax-y-reverse', `${(-offset * 0.58).toFixed(2)}px`);
            section.style.setProperty('--parallax-progress', clamped.toFixed(4));
        });

        ticking = false;
    };

    const requestUpdate = (): void => {
        if (ticking) {
            return;
        }

        ticking = true;
        window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
}

function initLanguagePicker(): void {
    const langButton = document.getElementById('langBtn') as HTMLButtonElement | null;
    const dropdown = document.getElementById('langDropdown');
    const flagCurrent = document.getElementById('flagCurrent');

    if (!langButton || !dropdown || !flagCurrent) {
        return;
    }

    const setActiveLanguage = (lang: string): void => {
        if (flagCurrent instanceof HTMLImageElement) {
            flagCurrent.src = `/images/${lang}.png`;
            flagCurrent.alt = lang.toUpperCase();
        }
    };

    const initialLang = document.documentElement.lang?.trim().toLowerCase() || 'en';
    setActiveLanguage(initialLang);

    langButton.addEventListener('click', () => {
        const open = dropdown.classList.toggle('open');
        langButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    dropdown.addEventListener('click', event => {
        const trigger = event.target as HTMLElement | null;
        const button = trigger?.closest<HTMLButtonElement>('button[data-lang]');
        if (!button) {
            return;
        }

        const { lang } = button.dataset;
        if (lang) {
            setActiveLanguage(lang);
        }
        dropdown.classList.remove('open');
        langButton.setAttribute('aria-expanded', 'false');
    });

    document.addEventListener('click', event => {
        const target = event.target as Node | null;
        if (!target) {
            return;
        }

        if (!dropdown.contains(target) && !langButton.contains(target)) {
            dropdown.classList.remove('open');
            langButton.setAttribute('aria-expanded', 'false');
        }
    });
}

function initDrawer(): void {
    const burger = document.getElementById('burger') as HTMLButtonElement | null;
    const drawer = document.getElementById('drawer');
    if (!burger || !drawer) {
        return;
    }

    const closeButton = drawer.querySelector<HTMLButtonElement>('[data-drawer-close]');

    const setOpen = (open: boolean): void => {
        drawer.classList.toggle('open', open);
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.body.classList.toggle('nav-open', open);
    };

    burger.addEventListener('click', () => {
        const isOpen = drawer.classList.contains('open');
        setOpen(!isOpen);
    });

    closeButton?.addEventListener('click', () => {
        setOpen(false);
    });

    drawer.addEventListener('click', event => {
        const trigger = event.target as HTMLElement | null;
        if (trigger === drawer) {
            setOpen(false);
            return;
        }
        const link = trigger?.closest<HTMLAnchorElement>('a');
        if (!link) {
            return;
        }

        const links = Array.from(drawer.querySelectorAll<HTMLAnchorElement>('a'));
        links.forEach(anchor => anchor.classList.remove('is-active'));
        link.classList.add('is-active');
        setOpen(false);
    });

    document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && drawer.classList.contains('open')) {
            setOpen(false);
        }
    });
}

function initContactDialog(): void {
    const dialog = document.getElementById('contact-dialog') as HTMLDialogElement | null;
    const form = document.getElementById('contact-form') as HTMLFormElement | null;
    const success = document.getElementById('contact-success') as HTMLElement | null;

    if (!dialog || !form || !success) {
        return;
    }

    const openers = document.querySelectorAll<HTMLElement>('[data-contact-open]');
    const closers = document.querySelectorAll<HTMLElement>('[data-contact-close]');

    const nameField = form.querySelector<HTMLInputElement>('input[name=\"name\"]');
    const emailField = form.querySelector<HTMLInputElement>('input[name=\"email\"]');
    const messageField = form.querySelector<HTMLTextAreaElement>('textarea[name=\"message\"]');
    const websiteField = form.querySelector<HTMLInputElement>('input[name=\"website\"]');
    const submitButton = form.querySelector<HTMLButtonElement>('button[type=\"submit\"]');
    const submitButtonText = submitButton?.textContent ?? '';

    if (!nameField || !emailField || !messageField) {
        return;
    }

    let successTimeout = 0;

    const {
        errorName: errorNameRaw,
        errorEmail: errorEmailRaw,
        errorMessage: errorMessageRaw,
        errorSubmit: errorSubmitRaw
    } = form.dataset;

    const errorMessages = {
        name: errorNameRaw ?? '',
        email: errorEmailRaw ?? '',
        message: errorMessageRaw ?? '',
        submit: errorSubmitRaw ?? ''
    };
    const minTextLength = 3;
    const minMessageLength = 25;
    type ContactServerField = 'name' | 'email' | 'message';
    const serverFieldToErrorId: Record<ContactServerField, string> = {
        name: 'contact-name',
        email: 'contact-email',
        message: 'contact-message'
    };

    const showError = (id: string, message = ''): void => {
        const target = form.querySelector<HTMLElement>(`.form-error[data-for=\"${id}\"]`);
        if (target) {
            target.textContent = message;
        }

        const control = form.querySelector<HTMLElement>(`#${id}`);
        if (control) {
            const hasError = Boolean(message);
            control.classList.toggle('is-invalid', hasError);
            control.setAttribute('aria-invalid', hasError ? 'true' : 'false');
        }
    };

    const clearErrors = (): void => {
        ['contact-name', 'contact-email', 'contact-message'].forEach(key => showError(key));
    };

    const focusFirstInvalid = (): void => {
        const firstInvalid = form.querySelector<HTMLElement>('[aria-invalid="true"]');
        firstInvalid?.focus();
    };

    const showServerValidation = async (response: Response): Promise<boolean> => {
        try {
            const payload = await response.json() as { fields?: Partial<Record<ContactServerField, string[]>> };
            if (!payload.fields) {
                return false;
            }

            let hasErrors = false;
            (Object.keys(serverFieldToErrorId) as ContactServerField[]).forEach(field => {
                if (payload.fields?.[field]?.length) {
                    hasErrors = true;
                    showError(serverFieldToErrorId[field], errorMessages[field]);
                }
            });

            if (hasErrors) {
                focusFirstInvalid();
            }

            return hasErrors;
        } catch {
            return false;
        }
    };

    const closeDialog = (): void => {
        if (dialog.open) {
            dialog.close();
        }
        window.clearTimeout(successTimeout);
        success.hidden = true;
    };

    const openDialog = (): void => {
        if (!dialog.open) {
            dialog.showModal();
        }
        success.hidden = true;
        clearErrors();
        window.setTimeout(() => {
            nameField.focus({ preventScroll: true });
        }, 50);
    };

    openers.forEach(button => {
        button.addEventListener('click', () => {
            trackEvent('contact_open', { source: button.getAttribute('data-cta') ?? 'button' });
            openDialog();
        });
    });

    closers.forEach(button => {
        button.addEventListener('click', () => {
            closeDialog();
            form.reset();
        });
    });

    dialog.addEventListener('cancel', event => {
        event.preventDefault();
        closeDialog();
    });

    [nameField, emailField, messageField].forEach(field => {
        field.addEventListener('input', () => showError(field.id));
    });

    const setSubmitting = (isSubmitting: boolean): void => {
        if (!submitButton) {
            return;
        }

        submitButton.disabled = isSubmitting;
        submitButton.textContent = isSubmitting ? 'Sending...' : submitButtonText;
    };

    form.addEventListener('submit', async event => {
        event.preventDefault();

        if (websiteField && websiteField.value.trim()) {
            return;
        }

        let valid = true;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (nameField.value.trim().length < minTextLength) {
            valid = false;
            showError('contact-name', errorMessages.name);
        } else {
            showError('contact-name');
        }

        if (!emailPattern.test(emailField.value.trim())) {
            valid = false;
            showError('contact-email', errorMessages.email);
        } else {
            showError('contact-email');
        }

        if (messageField.value.trim().length < minMessageLength) {
            valid = false;
            showError('contact-message', errorMessages.message);
        } else {
            showError('contact-message');
        }

        if (!valid) {
            focusFirstInvalid();
            return;
        }

        setSubmitting(true);

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify({
                    name: nameField.value.trim(),
                    email: emailField.value.trim(),
                    message: messageField.value.trim(),
                    website: websiteField?.value.trim() || null
                })
            });

            if (!response.ok) {
                if (response.status === 400 && await showServerValidation(response)) {
                    return;
                }

                throw new Error(`Contact request failed with status ${response.status}`);
            }

            trackEvent('contact_submit', { source: 'contact_form' });
            trackEvent('generate_lead', { method: 'contact_form' });

            success.hidden = false;
            form.reset();
            window.clearTimeout(successTimeout);
            successTimeout = window.setTimeout(() => {
                success.hidden = true;
                closeDialog();
            }, 3200);
        } catch (error) {
            console.error('Failed to submit contact form', error);
            showError('contact-message', errorMessages.submit);
        } finally {
            setSubmitting(false);
        }
    });
}

interface NewsletterData {
    id: string;
    slug: string;
    name: string;
    description?: string | null;
    topic?: string | null;
}

interface NewsletterSignupResultData {
    subscriberId: string;
    newsletterIds: string[];
}

function initCookieBanner(): void {
    const banner = document.getElementById('cookie-banner');
    if (!banner) {
        return;
    }

    const acceptButton = banner.querySelector<HTMLButtonElement>('[data-cookie-accept]');
    const declineButton = banner.querySelector<HTMLButtonElement>('[data-cookie-decline]');
    const analyticsId = banner.getAttribute('data-analytics-id') ?? '';
    const settingsButtons = document.querySelectorAll<HTMLButtonElement>('[data-cookie-settings]');

    initializeConsentDefaults();

    const hideBanner = (): void => {
        banner.setAttribute('hidden', 'true');
    };

    const showBanner = (): void => {
        banner.removeAttribute('hidden');
        window.setTimeout(() => {
            acceptButton?.focus({ preventScroll: true });
        }, 50);
    };

    if (!analyticsId) {
        hideBanner();
        settingsButtons.forEach(button => {
            button.hidden = true;
        });
        return;
    }

    acceptButton?.addEventListener('click', () => {
        writeStoredConsent(true);
        hideBanner();
        loadAnalytics(analyticsId);
    });

    declineButton?.addEventListener('click', () => {
        writeStoredConsent(false);
        hideBanner();
        disableAnalytics(analyticsId);
    });

    settingsButtons.forEach(button => {
        button.addEventListener('click', () => {
            showBanner();
        });
    });

    const stored = readStoredConsent();
    if (stored?.analytics === true) {
        hideBanner();
        loadAnalytics(analyticsId);
        return;
    }

    if (stored?.analytics === false) {
        hideBanner();
        disableAnalytics(analyticsId);
    } else {
        disableAnalytics(analyticsId);
        showBanner();
    }
}

function initNewsletterDialog(): void {
    const openButton = document.getElementById('newsletter-open') as HTMLButtonElement | null;
    const openers = document.querySelectorAll<HTMLElement>('[data-newsletter-open]');
    const dialog = document.getElementById('newsletter-dialog') as HTMLDialogElement | null;
    const dialogForm = document.getElementById('newsletter-dialog-form') as HTMLFormElement | null;
    const optionsWrap = document.getElementById('newsletter-options') as HTMLElement | null;
    const statusEl = document.getElementById('newsletter-dialog-status') as HTMLElement | null;
    const firstNameInput = document.getElementById('newsletter-dialog-first-name') as HTMLInputElement | null;
    const emailInput = document.getElementById('newsletter-dialog-email-input') as HTMLInputElement | null;

    if (!dialog || !dialogForm || !optionsWrap || !statusEl || !firstNameInput || !emailInput) {
        return;
    }

    const closeButtons = dialog.querySelectorAll<HTMLElement>('[data-newsletter-close]');

    const requiredText = dialogForm.dataset.requiredText ?? 'Email is required.';
    const invalidText = dialogForm.dataset.invalidText ?? 'Enter a valid email address.';
    const loadingText = dialog.dataset.loadingText ?? 'Loading…';
    const errorText = dialog.dataset.errorText ?? 'Something went wrong.';
    const emptyText = dialog.dataset.emptyText ?? 'No newsletters.';
    const selectText = dialog.dataset.selectText ?? 'Select at least one.';
    const conflictText = dialog.dataset.conflictText ?? 'Already subscribed.';

    const emailPattern = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
    let cachedNewsletters: NewsletterData[] | null = null;
    let cachedLanguage = '';

    const normalizeLanguage = (value: string): string => {
        const normalized = value.trim().toLowerCase();
        if (normalized.startsWith('pl')) {
            return 'pl';
        }
        return 'en';
    };

    const getLanguage = (): string =>
        normalizeLanguage(document.documentElement.lang || 'en');

    const openDialog = (): void => {
        if (!dialog.open) {
            dialog.showModal();
        }
        statusEl.textContent = '';
        document.body.classList.add('dialog-open');
        dialogForm.querySelectorAll<HTMLButtonElement>('button[type="submit"]').forEach(button => {
            button.disabled = false;
        });
    };

    const closeDialog = (): void => {
        if (dialog.open) {
            dialog.close();
        }
        document.body.classList.remove('dialog-open');
        statusEl.textContent = '';
        dialogForm.reset();
    };

    const renderOptions = (newsletters: NewsletterData[]): void => {
        if (!newsletters.length) {
            optionsWrap.innerHTML = `<p class=\"muted empty-state\">${emptyText}</p>`;
            return;
        }

        const fragment = document.createDocumentFragment();
        newsletters.forEach(item => {
            const option = document.createElement('label');
            option.className = 'newsletter-option';

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.name = 'newsletter';
            checkbox.value = item.id;

            const body = document.createElement('div');
            const title = document.createElement('h4');
            title.textContent = item.name;
            body.appendChild(title);

            if (item.description) {
                const desc = document.createElement('p');
                desc.textContent = item.description;
                body.appendChild(desc);
            }

            option.appendChild(checkbox);
            option.appendChild(body);
            fragment.appendChild(option);
        });

        optionsWrap.replaceChildren(fragment);
    };

    const loadNewsletters = async (): Promise<void> => {
        const language = getLanguage();
        if (cachedNewsletters && cachedLanguage === language) {
            renderOptions(cachedNewsletters);
            return;
        }

        optionsWrap.innerHTML = `<p class=\"muted\">${loadingText}</p>`;

        try {
            const response = await fetch('/api/newsletters', {
                headers: cultureHeaders(language)
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const data = await response.json() as NewsletterData[];
            cachedNewsletters = data ?? [];
            cachedLanguage = language;
            renderOptions(cachedNewsletters);
        } catch (error) {
            console.error('Failed to load newsletters', error);
            optionsWrap.innerHTML = `<p class=\"muted\">${errorText}</p>`;
        }
    };

    const handleOpen = (): void => {
        dialogForm.reset();
        openDialog();
        void loadNewsletters();
        window.setTimeout(() => {
            emailInput.focus({ preventScroll: true });
        }, 50);
    };

    openButton?.addEventListener('click', handleOpen);
    openers.forEach(opener => opener.addEventListener('click', handleOpen));

    closeButtons.forEach(button => {
        button.addEventListener('click', closeDialog);
    });

    dialog.addEventListener('cancel', event => {
        event.preventDefault();
        closeDialog();
    });

    dialogForm.addEventListener('submit', async event => {
        event.preventDefault();
        statusEl.textContent = '';
        const emailValue = emailInput.value.trim();
        if (!emailValue) {
            statusEl.textContent = requiredText;
            return;
        }

        if (!emailPattern.test(emailValue)) {
            statusEl.textContent = invalidText;
            return;
        }

        const selected = Array.from(dialogForm.querySelectorAll<HTMLInputElement>('input[name=\"newsletter\"]:checked'))
            .map(input => input.value);

        if (!selected.length) {
            statusEl.textContent = selectText;
            return;
        }

        const language = getLanguage();
        try {
            const response = await fetch('/api/newsletters/subscriptions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    email: emailValue,
                    firstName: firstNameInput.value.trim() || null,
                    newsletterIds: selected,
                    language
                })
            });

            if (response.status === 409) {
                statusEl.textContent = conflictText;
                return;
            }

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            await response.json() as NewsletterSignupResultData;
            trackEvent('newsletter_signup', { method: 'dialog' });
            trackEvent('sign_up', { method: 'newsletter', selection_count: selected.length });
            closeDialog();
            window.location.href = newsletterConfirmPath();
        } catch (error) {
            console.error('Failed to subscribe', error);
            statusEl.textContent = errorText;
        }
    });
}

function bootstrap(): void {
    initHeroVisuals();
    initMenuIndicator();
    initMegaMenu();
    initParallaxSections();
    initFooterYear();
    initLanguagePicker();
    initDrawer();
    initCookieBanner();
    initContactDialog();
    initAnalyticsEvents();
    initNewsletterDialog();
    initFaqSchema();
}

bootstrap();
